import React from 'react';
import { useData } from '../../../hooks/useData';
import { useApp } from '../../../contexts/AppContext';
import { TABLES } from '../../../constants';
import { ERROR_SEVERITY, ERROR_CATEGORY, useErrorLogging } from '../../../utils/errorLogging';
import { useSaveIndicator } from '../../../utils/useSaveIndicator';
import pageStyles from '../../../styles/pages.module.css';

// Keep business logic validation separate from type validation
const validateField = (name, value) => {
  switch (name) {
    case 'num_floors':
      if (value < 1) return 'מספר הקומות חייב להיות חיובי';
      break;
    case 'total_area':
    case 'plot_size':
    case 'total_budget':
      if (value < 0) return 'הערך חייב להיות חיובי';
      break;
    default:
      break;
  }
  return null;
};

const HighLevelInfo = () => {
  const { currentProject } = useApp();
  const { logError } = useErrorLogging();

  const {
    data: projectInfo,
    error: projectError,
    isLoading: projectLoading,
    update,
  } = useData(TABLES.PROJECT_INFO, {
    enabled: Boolean(currentProject?.id),
    filter: { id: currentProject?.id },
    realtime: true,
  });

  const {
    data: constructionTypes,
    isLoading: loadingConstructionTypes,
    error: constructionTypesError,
  } = useData(TABLES.REFERENCE_CONSTRUCTION_TYPES);

  const {
    data: projectPhases,
    isLoading: loadingProjectPhases,
    error: projectPhasesError,
  } = useData(TABLES.REFERENCE_PROJECT_PHASES, {
    select: 'id,name,display_order',
    realtime: false,
  });

  const [fieldErrors, setFieldErrors] = React.useState({});
  const [formData, setFormData] = React.useState(null);
  const [savingField, setSavingField] = React.useState(null);
  const { savedField, markSaved } = useSaveIndicator();

  React.useEffect(() => {
    if (projectInfo?.[0]) {
      const data = projectInfo[0];
      setFormData({
        address: data.address || '',
        construction_type_id: data.construction_type_id || '',
        plot_size: data.plot_size || '',
        total_area: data.total_area || '',
        num_floors: data.num_floors || '',
        project_phase_id: data.project_phase_id || '',
        start_date: data.start_date || '',
        estimated_completion: data.estimated_completion || '',
        total_budget: data.total_budget || 0,
      });
    }
  }, [projectInfo]);

  const handleChange = React.useCallback(e => {
    const { name, value } = e.target;

    // Remove any existing error
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    // Store the raw value
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleBlur = React.useCallback(
    async e => {
      const { name, value } = e.target;

      // Validate before saving
      const error = validateField(name, value);
      if (error) {
        setFieldErrors(prev => ({ ...prev, [name]: error }));
        return;
      }

      // Skip if value hasn't changed
      const originalValue = projectInfo?.[0]?.[name];
      if (value === originalValue || (value === '' && !originalValue)) {
        return;
      }

      try {
        setSavingField(name);
        await update({
          id: currentProject.id,
          data: { [name]: value },
        });
        markSaved(name);
      } catch (error) {
        if (error.cause?.type === 'ValidationError') {
          setFieldErrors(prev => ({ ...prev, ...error.cause.errors }));
        } else {
          logError({
            severity: ERROR_SEVERITY.ERROR,
            category: ERROR_CATEGORY.DATA_MUTATION,
            message: `Failed to save ${name}`,
            error,
          });
        }
      } finally {
        setSavingField(null);
      }
    },
    [currentProject?.id, projectInfo, update, logError],
  );

  const formatCurrency = React.useCallback(value => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  if (projectLoading || loadingConstructionTypes || loadingProjectPhases) {
    return <div>טוען מידע...</div>;
  }

  if (projectError || constructionTypesError || projectPhasesError) {
    return <div>שגיאה בטעינת מידע הפרוייקט</div>;
  }

  if (!currentProject) {
    return <div>אנא בחר פרוייקט</div>;
  }

  return (
    <>
      {/* Summary cards - one field per card like BudgetPage */}
      <div className={pageStyles.summarySection}>
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            כתובת
            {savedField === 'address' && <span className={pageStyles.saveIndicator}> ✓</span>}
          </h3>
          <input
            type="text"
            name="address"
            className={pageStyles.infoInput}
            value={formData?.address || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={savingField === 'address'}
            placeholder="הזן כתובת"
          />
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            סוג בנייה
            {savedField === 'construction_type_id' && (
              <span className={pageStyles.saveIndicator}> ✓</span>
            )}
          </h3>
          <select
            name="construction_type_id"
            className={pageStyles.infoSelect}
            value={formData?.construction_type_id || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={savingField === 'construction_type_id' || loadingConstructionTypes}
          >
            <option value="">בחר סוג בנייה</option>
            {constructionTypes?.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {fieldErrors.construction_type_id && (
            <div className={pageStyles.fieldError}>{fieldErrors.construction_type_id}</div>
          )}
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            מצב הפרוייקט
            {savedField === 'project_phase_id' && (
              <span className={pageStyles.saveIndicator}> ✓</span>
            )}
          </h3>
          <select
            name="project_phase_id"
            className={pageStyles.infoSelect}
            value={formData?.project_phase_id || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={savingField === 'project_phase_id'}
          >
            <option value="">בחר מצב</option>
            {projectPhases
              ?.sort((a, b) => a.display_order - b.display_order)
              .map(phase => (
                <option key={phase.id} value={phase.id}>
                  {phase.name}
                </option>
              ))}
          </select>
          {fieldErrors.project_phase_id && (
            <div className={pageStyles.fieldError}>{fieldErrors.project_phase_id}</div>
          )}
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            תקציב הפרוייקט
            {savedField === 'total_budget' && <span className={pageStyles.saveIndicator}> ✓</span>}
          </h3>
          <input
            type="text"
            name="total_budget"
            className={pageStyles.infoInput}
            value={formatCurrency(formData?.total_budget || 0)}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={savingField === 'total_budget'}
          />
          {fieldErrors.total_budget && (
            <div className={pageStyles.fieldError}>{fieldErrors.total_budget}</div>
          )}
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            תאריך תחילת פרוייקט
            {savedField === 'start_date' && <span className={pageStyles.saveIndicator}> ✓</span>}
          </h3>
          <input
            type="date"
            name="start_date"
            className={pageStyles.infoInput}
            value={formData?.start_date || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={savingField === 'start_date'}
          />
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            תאריך סיום משוער
            {savedField === 'estimated_completion' && (
              <span className={pageStyles.saveIndicator}> ✓</span>
            )}
          </h3>
          <input
            type="date"
            name="estimated_completion"
            className={pageStyles.infoInput}
            value={formData?.estimated_completion || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={savingField === 'estimated_completion'}
          />
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            מספר קומות
            {savedField === 'num_floors' && <span className={pageStyles.saveIndicator}> ✓</span>}
          </h3>
          <input
            type="number"
            name="num_floors"
            className={pageStyles.infoInput}
            value={formData?.num_floors || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            min="1"
            step="1"
            disabled={savingField === 'num_floors'}
          />
          {fieldErrors.num_floors && (
            <div className={pageStyles.fieldError}>{fieldErrors.num_floors}</div>
          )}
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            שטח בנוי (מ״ר)
            {savedField === 'total_area' && <span className={pageStyles.saveIndicator}> ✓</span>}
          </h3>
          <input
            type="number"
            name="total_area"
            className={pageStyles.infoInput}
            value={formData?.total_area || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            step="0.01"
            disabled={savingField === 'total_area'}
          />
          {fieldErrors.total_area && (
            <div className={pageStyles.fieldError}>{fieldErrors.total_area}</div>
          )}
        </div>

        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>
            שטח מגרש (מ״ר)
            {savedField === 'plot_size' && <span className={pageStyles.saveIndicator}> ✓</span>}
          </h3>
          <input
            type="number"
            name="plot_size"
            className={pageStyles.infoInput}
            value={formData?.plot_size || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            step="0.01"
            disabled={savingField === 'plot_size'}
          />
          {fieldErrors.plot_size && (
            <div className={pageStyles.fieldError}>{fieldErrors.plot_size}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default HighLevelInfo;
